import { NotificationTypes } from "../Enums/NotificationTypes";
import { QueryParameter, DMSMenu, Mapping, PageContext, Util } from "Core";
import { App } from "./App";

/**
 * ## Navigation, dialogs and notifications.
 *
 * Contains methods for navigation and displaying of dialogs and notifications.
 * @since 4.0.0
 */
export class Navigation {
    public static root: string;

    public static setRoot(root: string) {
        this.root = root;
    }

    /**
     * Opens the url in a new window.
     * @param url Url to be openend.
     * @param urlMode Parameters.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // open an url in a new window.
     * DMS.Navigation.openWindow('~/DMS/Test', 'width=1024,height=768');
     * ```
     */
    public static openWindow(url: string, urlMode: string) {
        if (Util.stringIsNullOrEmpty(urlMode)) {
            urlMode = "";
        }

        // if (urlMode.indexOf("scrollbars") == -1) {
        //     urlMode += (urlMode.length > 0 ? "," : "") + "scrollbars=yes";
        // }
        //if (urlMode.indexOf("resizable") == -1) {
        //    urlMode += (urlMode.length > 0 ? "," : "") + "resizable=yes";
        //}
        // if (urlMode.indexOf("status") == -1) {
        //     urlMode += (urlMode.length > 0 ? "," : "") + "status=no";
        // }
        // if (urlMode.indexOf("toolbar") == -1) {
        //     urlMode += (urlMode.length > 0 ? "," : "") + "toolbar=no";
        // }
        // if (urlMode.indexOf("menubar") == -1) {
        //     urlMode += (urlMode.length > 0 ? "," : "") + "menubar=no";
        // }
        // if (urlMode.indexOf("location") == -1) {
        //     urlMode += (urlMode.length > 0 ? "," : "") + "location=no";
        // }
        if (this.isAbsoluteUrl(url)) {
            window.open(url, "_blank", urlMode);
        } else {
            url = this.getUrl(url, true);
            window.open("/" + url, "_blank", urlMode);
        }
    }

    /**
     * Navigates to the url.
     * @param url Url to be navigated to.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // navigate to the url
     * DMS.Navigation.navigate('~/DMS/Test');
     * ```
     */
    public static navigate(url: string, updateActiveMenu: boolean = true) {
        if (this.isAbsoluteUrl(url)) {
            this.navigateBrowser(url);
        } else {
            if (url.startsWith("javascript")) {
                this.navigateBrowser(url);
            } else {
                App.current.router.navigate(this.getUrl(url, false));
                if (updateActiveMenu) {
                    DMSMenu.setActiveItemByUrl(url);
                }
            }
        }
    }

    public static back() {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            this.navigateBrowser("~/");
        }
    }

    /**
     * Navigates to the url.
     * @param url Url to be navigated to.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // navigate to the url
     * DMS.Navigation.navigate('~/DMS/Test');
     * ```
     */
    public static navigateBrowser(url: string) {
        if (url.startsWith("javascript")) {
            window.location.href = url;
            return;
        }

        url = this.getUrl(url, true);
        if (this.isAbsoluteUrl(url)) {
            window.location.href = url;
        } else {
            window.location.href = "/" + url;
        }
    }

    /**
     * Shows a confirmation popup.
     *
     * This call is synchronous and blocks the control flow.
     * The popup uses the default styling of a browser popup.
     * @param text Text for popup. Html is not allowed.
     * @param title Title (not used in this implementation).
     * @returns Returns true if the user clicked OK, otherwise false.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * var result = DMS.Navigation.confirm('Do you really want to accept?');
     * ```
     */
    public static confirm(text: string, title?: string): boolean {
        return confirm(text);
    }

    /**
     * Shows a confirmation popup.
     *
     * This call is asynchronous and does not block the control flow.
     * The popup is styled in the dms style.
     * @param text Text for the popup. Html is allowed.
     * @param title Title for the popup.
     * @returns A promise with the result.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.confirmAsync('Do you really want to accept?', 'Question').then(function(result) {
     *      // result is set here.
     * });
     * ```
     */
    public static confirmAsync(text: string, title?: string): Promise<boolean> {
        let dialog = new kendo.ui.Confirm($("<div/>")[0], <any>{
            title: title,
            content: text,
            minWidth: 300,
            visible: true,
        });

        return new Promise<boolean>((resolve: (val: boolean) => void, reject) => {
            return dialog.result.done(() => resolve(true)).fail(() => resolve(false));
        });
    }

    /**
     * Shows an error popup.
     *
     * This call is synchronous and blocks the control flow.
     * The popup uses the default styling of a browser popup.
     * @param text Text for popup. Html is not allowed.
     * @param title Title (not used in this implementation).
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.error('Error: Missing field.');
     * ```
     */
    public static error(text: string, title?: string): void {
        alert(text);
    }

    /**
     * Shows an error popup.
     *
     * This call is asynchronous and does not block the control flow.
     * The popup is styled in the dms style.
     * @param text Text for the popup. Html is allowed.
     * @param title Title for the popup.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.errorAsync('Error: Missing field.', 'Error').then(function() {
     *      // code after popup was closed.
     * });
     * ```
     */
    public static errorAsync(text: string, title?: string): Promise<void> {
        return new Promise<void>(function (resolve, reject) {
            new kendo.ui.Alert($("<div/>")[0], <any>{
                title: title || " ",
                content: text,
                visible: true,
                close: resolve,
            });
        });
    }

    /**
     * Shows a prompt popup.
     *
     * This call is synchronous and blocks the control flow.
     * The popup uses the default styling of a browser popup.
     * @param text Text for popup. Html is not allowed.
     * @param defaultValue DefaultValue for the prompt field.
     * @param title Title (not used in this implementation).
     * @returns The text input from the user.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * var input = DMS.Navigation.prompt('Please insert your name.', 'MyName');
     * alert(input); // entered value is returned.
     * ```
     */
    public static prompt(text: string, defaultValue: string, title?: string): string {
        return prompt(text, defaultValue);
    }

    /**
     * Shows an prompt popup.
     *
     * This call is asynchronous and does not block the control flow.
     * The popup is styled in the dms style.
     * @param text Text for the popup. Html is allowed.
     * @param defaultValue DefaultValue for the prompt field.
     * @param title Title for the popup.
     * @returns Promise which resolves with the input. Rejected if the user clicks cancel.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.promptAsync('Insert your name.', 'MyName', 'Enter name').then(function(input) {
     *      alert(input); // entered value is returned.
     * });
     * ```
     */
    public static promptAsync(text: string, defaultValue: string, title?: string): Promise<string> {
        return new Promise<string>(function (resolve, reject) {
            new kendo.ui.Prompt($("<div/>")[0], <any>{
                title: title,
                content: text,
                value: defaultValue,
                visible: true,
            }).result
                .done((text: string) => resolve(text))
                .fail(() => reject());
        });
    }

    private static notification: kendo.ui.Notification;

    /**
     * Initialize the {Navigation} components.
     * @hidden
     */
    public static initialize(): void {
        this.notification = new kendo.ui.Notification($("#dms-notification")[0], {
            position: {
                top: 70,
                bottom: null,
                right: 40,
                left: null,
            },
            templates: [
                {
                    type: "info",
                    template: `
                        <div class="dms-notification-content dms-notification-info">
                            <div class="dms-notification-icon"><i class="far fa-circle-info fa-xl"></i></div>
                            <div>
                                <h4>#= data.title #</h4>
                                # if(data.content) { # <span>#= data.content #</span> # } #
                            </div>
                        </div>
                        `,
                },
                {
                    type: "error",
                    template: `
                        <div class="dms-notification-content dms-notification-error">
                            <div class="dms-notification-icon"><i class="far fa-circle-exclamation fa-xl"></i></div>
                            <div>
                                <h4>#= data.title #</h4>
                                # if(data.content) { # <span>#= data.content #</span> # } #
                            </div>
                        `,
                },
                {
                    type: "success",
                    template: `
                        <div class="dms-notification-content dms-notification-success">
                            <div class="dms-notification-icon"><i class="far fa-circle-check fa-xl"></i></div>
                            <div>
                                <h4>#= data.title #</h4>
                                # if(data.content) { # <span>#= data.content #</span> # } #
                            </div>
                        </div>
                        `,
                },
            ],
            width: 435,
            button: true,
        });
    }

    /**
     * Show a notification.
     *
     * Notifications are small hints on the right side of the screen.
     * @param title The title for the notification.
     * @param notificationType Type of notification. Errors stay until the user closes them. Success and Info notifications hide after 5 seconds.
     * @param text Optional detailed text for the notification.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.notify('Row saved successfully.', DMS.Enums.NotificationTypes.Success);
     * DMS.Navigation.notify('Error', DMS.Enums.NotificationTypes.Error, 'An error occurred while saving the row.');
     * ```
     */
    public static notify(title: string, notificationType: NotificationTypes, text?: string): void {
        if (!this.notification) {
            return;
        }

        switch (notificationType) {
            case NotificationTypes.Error:
                this.notification.options.autoHideAfter = 0;
                this.notification.error({ content: text, title: title });
                break;
            case NotificationTypes.Success:
                this.notification.options.autoHideAfter = 5000;
                this.notification.success({ content: text, title: title });
                break;
            case NotificationTypes.Info:
                this.notification.options.autoHideAfter = 5000;
                this.notification.info({ content: text, title: title });
                break;
        }
    }

    /**
     *
     * @param url
     * @since 4.0.0
     */
    public static getUrl(url: string, appendBaseUrl: boolean) {
        // check if path is absolute
        if (this.isAbsoluteUrl(url)) {
            return url;
        }

        if (url.indexOf("~") == 0) {
            url = url.substring(1);
        }

        if (url.startsWith(this.root)) {
            url = url.substring(this.root.length);
        }

        // check if dms-path is valid
        if (url.indexOf("/") != 0) {
            url = "/" + url;
        }

        if (appendBaseUrl) {
            let baseUrl = this.root;
            if (baseUrl == undefined) {
                baseUrl = "/";
            }
            return (baseUrl.slice(0, -1) + url).slice(1);
        }
        return url.slice(1);
    }

    public static isAbsoluteUrl(url: string) {
        // check if path is absolute
        let pat = /^https?:\/\//i;
        return pat.test(url);
    }

    public static getCurrentUrl() {
        let path = window.location.pathname + window.location.search + window.location.hash;
        // Remove root from start if not '/'
        if (this.root && this.root !== "/" && path.startsWith(this.root)) {
            path = path.substring(this.root.length);
            if (!path.startsWith("/")) {
                path = "/" + path;
            }
        }
        return path;
    }

    /**
     * Opens a popup and initializes a mapping as content.
     * @param mappingName Name of the mapping.
     * @param parameters Query parameters to be added i.e. "ID=1".
     * @param width The width of the window.
     * @param height The height of the window.
     * @param title The optional title of the window.
     * @param paramOptions Additional options to overwrite kendo window options.
     * @since 5.1.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.openMappingInPopup('int_Menu_Form', 'MenuID=1', 1024, 768, 'Edit menu');
     * ```
     */
    public static openMappingInPopup(
        mappingName: string,
        parameters: string,
        width: string | number,
        height: string | number,
        title: string,
        paramOptions: any = {},
    ) {
        const element = $(`<div class="dms-mapping-popup">`);
        $("body").append(element);

        QueryParameter.addFromQueryString(parameters, false);

        let mapping: Mapping = null;

        const options: kendo.ui.WindowOptions = {
            activate: (e) => {
                PageContext.initializeMappingInContainer($(e.sender.element), mappingName).then((m) => {
                    mapping = m;
                    mapping.setHeight($(e.sender.element).height());
                });
            },
            resize: (e) => {
                if (mapping) {
                    mapping.setHeight($(e.sender.element).height());
                }
            },
            deactivate: (e) => {
                if (mapping) {
                    mapping.dispose();
                }
                $("body").find(element).remove();
                if ((<any>window).dmsPopup) {
                    (<any>window).dmsPopup.destroy();
                }
            },
            modal: true,
            width: width,
            height: height,
            title: title,
            position: {
                top: 50,
                left: 0,
            },
        };
        if (paramOptions) {
            Object.assign(options, paramOptions);
        }

        const dmsPopup = ((<any>window).dmsPopup = new kendo.ui.Window(element[0], options));
        dmsPopup.center();
    }

    /**
     * Closes the currently visible popup.
     * @since 5.1.0.0
     * @example
     * ```JavaScript
     * DMS.Navigation.closePopup();
     * ```
     */
    public static closePopup(): void {
        if ((<any>window).dmsPopup) {
            (<any>window).dmsPopup.close();
        }
    }
}
